import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Block as BlockIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAppData } from '../../context/AppDataContext';
import {
  createPlanDiscountVoucher,
  disablePlanDiscountVoucher,
  getPlanDiscountVouchers,
} from '../../services/planDiscountVoucherService';
import { formatUtcDate } from '../../utils/dateFormatUtils';
import DataTable from '../common/DataTable';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareDateRangePicker from '../common/SalovareDateRangePicker';
import ConfirmDialog from '../common/ConfirmDialog';
import { BRAND } from '../../theme/brandTokens';

const ROWS_PER_PAGE = 25;

const PLACEHOLDER_COLOR = '#999';

const inputWrapperSx = {
  '& input': {
    width: '100%',
    padding: '12px 16px',
    border: `2px solid ${BRAND.line}`,
    borderRadius: '8px',
    fontSize: '16px',
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'border-color 0.3s ease',
    backgroundColor: '#fff',
    boxSizing: 'border-box',
  },
  '& input::placeholder': {
    color: PLACEHOLDER_COLOR,
    opacity: 1,
  },
  '& input:focus': {
    borderColor: BRAND.accent,
  },
  '& input.error': {
    borderColor: '#f44336',
  },
};

const selectSx = {
  height: '48px !important',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: BRAND.line,
    borderWidth: '2px !important',
    borderRadius: '8px !important',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: `${BRAND.accent} !important`,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: `${BRAND.accent} !important`,
    borderWidth: '2px !important',
  },
  '&.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: '#f44336 !important',
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 16px',
    fontSize: '16px',
  },
};

const toUtcStartOfDay = (date) => {
  if (!date) {
    return null;
  }

  const normalized = new Date(date);
  return new Date(Date.UTC(
    normalized.getFullYear(),
    normalized.getMonth(),
    normalized.getDate(),
    0,
    0,
    0,
    0,
  )).toISOString();
};

const toUtcEndOfDay = (date) => {
  if (!date) {
    return null;
  }

  const normalized = new Date(date);
  return new Date(Date.UTC(
    normalized.getFullYear(),
    normalized.getMonth(),
    normalized.getDate(),
    23,
    59,
    59,
    999,
  )).toISOString();
};

const getUtcDateOnlyTimestamp = (date) => Date.UTC(
  date.getFullYear(),
  date.getMonth(),
  date.getDate(),
);

const getStatusColor = (status) => {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Scheduled':
      return 'info';
    case 'Expired':
      return 'default';
    case 'Disabled':
      return 'warning';
    default:
      return 'default';
  }
};

const PlanDiscountVouchersPanel = () => {
  const { t } = useTranslation();
  const { stores } = useAppData();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [dateRangeError, setDateRangeError] = useState(false);
  const [disableTarget, setDisableTarget] = useState(null);
  const [disableLoading, setDisableLoading] = useState(false);
  const [form, setForm] = useState({
    storeId: '',
    discountPercent: '',
    startsAt: null,
    endsAt: null,
  });

  const loadVouchers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getPlanDiscountVouchers({
        pageIndex: page - 1,
        itemsPerPage: ROWS_PER_PAGE,
      });
      setRows(result.data);
      setTotalItems(result.totalItems);
    } catch (err) {
      console.error('Failed to load plan discount vouchers:', err);
      setError(err?.response?.data?.message || t('planDiscountVouchers.error.loading'));
    } finally {
      setLoading(false);
    }
  }, [page, t]);

  useEffect(() => {
    loadVouchers();
  }, [loadVouchers]);

  const resetForm = () => {
    setForm({
      storeId: '',
      discountPercent: '',
      startsAt: null,
      endsAt: null,
    });
    setCreateError(null);
    setFieldErrors({});
    setDateRangeError(false);
  };

  const validateForm = () => {
    const errors = {};
    const discountPercent = Number(form.discountPercent);
    const todayUtc = getUtcDateOnlyTimestamp(new Date());

    if (!form.storeId) {
      errors.storeId = 'planDiscountVouchers.form.errors.storeRequired';
    }

    if (!String(form.discountPercent ?? '').trim()) {
      errors.discountPercent = 'planDiscountVouchers.form.errors.discountRequired';
    } else if (!Number.isFinite(discountPercent) || discountPercent < 1 || discountPercent > 100) {
      errors.discountPercent = 'planDiscountVouchers.form.errors.discountInvalid';
    }

    if (!form.startsAt) {
      errors.duration = 'planDiscountVouchers.form.errors.startRequired';
    } else if (getUtcDateOnlyTimestamp(form.startsAt) < todayUtc) {
      errors.duration = 'planDiscountVouchers.form.errors.startInPast';
    } else if (form.endsAt && getUtcDateOnlyTimestamp(form.endsAt) < getUtcDateOnlyTimestamp(form.startsAt)) {
      errors.duration = 'planDiscountVouchers.form.errors.dateRange';
    }

    return errors;
  };

  const handleOpenCreate = () => {
    resetForm();
    setCreateDialogOpen(true);
  };

  const handleCloseCreate = () => {
    if (createLoading) {
      return;
    }

    setCreateDialogOpen(false);
    resetForm();
  };

  const handleCreate = async () => {
    const errors = validateForm();
    setFieldErrors(errors);
    setDateRangeError(Boolean(errors.duration));

    if (Object.keys(errors).length > 0) {
      return;
    }

    const discountPercent = Number(form.discountPercent);

    setCreateLoading(true);
    setCreateError(null);

    try {
      await createPlanDiscountVoucher({
        storeId: form.storeId,
        discountPercent,
        startsAtUtc: toUtcStartOfDay(form.startsAt),
        endsAtUtc: toUtcEndOfDay(form.endsAt),
      });
      setCreateDialogOpen(false);
      resetForm();
      await loadVouchers();
    } catch (err) {
      console.error('Failed to create plan discount voucher:', err);
      setCreateError(err?.response?.data?.message || t('planDiscountVouchers.form.errors.create'));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleConfirmDisable = async () => {
    if (!disableTarget) {
      return;
    }

    setDisableLoading(true);

    try {
      await disablePlanDiscountVoucher(disableTarget.id);
      setDisableTarget(null);
      await loadVouchers();
    } catch (err) {
      console.error('Failed to disable plan discount voucher:', err);
      setError(err?.response?.data?.message || t('planDiscountVouchers.error.disable'));
    } finally {
      setDisableLoading(false);
    }
  };

  const columns = useMemo(() => [
    {
      field: 'code',
      header: t('planDiscountVouchers.table.code'),
      mobilePrimary: true,
      render: (row) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, wordBreak: 'break-all' }}>
          {row.code ?? row.Code}
        </Typography>
      ),
    },
    {
      field: 'storeName',
      header: t('planDiscountVouchers.table.store'),
      render: (row) => row.storeName ?? row.StoreName ?? '—',
    },
    {
      field: 'discountPercent',
      header: t('planDiscountVouchers.table.discount'),
      render: (row) => `${row.discountPercent ?? row.DiscountPercent}%`,
    },
    {
      field: 'startsAtUtc',
      header: t('planDiscountVouchers.table.startsAt'),
      render: (row) => formatUtcDate(row.startsAtUtc ?? row.StartsAtUtc),
    },
    {
      field: 'endsAtUtc',
      header: t('planDiscountVouchers.table.endsAt'),
      render: (row) => formatUtcDate(row.endsAtUtc ?? row.EndsAtUtc),
    },
    {
      field: 'status',
      header: t('planDiscountVouchers.table.status'),
      render: (row) => {
        const status = row.status ?? row.Status;
        return (
          <Chip
            size="small"
            label={t(`planDiscountVouchers.status.${status}`, status)}
            color={getStatusColor(status)}
          />
        );
      },
    },
    {
      field: 'createdAt',
      header: t('planDiscountVouchers.table.createdAt'),
      render: (row) => formatUtcDate(row.createdAt ?? row.CreatedAt),
    },
    {
      field: 'actions',
      header: t('planDiscountVouchers.table.actions'),
      mobileRole: 'actions',
      render: (row) => {
        const status = row.status ?? row.Status;
        const canDisable = status !== 'Disabled' && status !== 'Expired';

        return (
          <Tooltip title={t('planDiscountVouchers.actions.disable')}>
            <span>
              <IconButton
                size="small"
                disabled={!canDisable || disableLoading}
                onClick={() => setDisableTarget(row)}
              >
                <BlockIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ], [disableLoading, t]);

  return (
    <>
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            mb: 2,
          }}
        >
          <PrimaryActionButton
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {t('planDiscountVouchers.actions.create')}
          </PrimaryActionButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <DataTable
          data={rows}
          loading={loading}
          columns={columns}
          searchable={false}
          filterable={false}
          pagination
          rowsPerPage={ROWS_PER_PAGE}
          currentPage={page}
          totalCount={totalItems}
          onPageChange={setPage}
          emptyMessage={t('planDiscountVouchers.empty')}
        />
      </Box>

      <ScrollableDialog
        open={createDialogOpen}
        onClose={handleCloseCreate}
        title={t('planDiscountVouchers.form.title')}
        showCloseButton
        disableScrollLock
        maxWidth="sm"
        fullWidth
        actionsSx={{ justifyContent: 'space-between' }}
        actions={(
          <>
            <Button onClick={handleCloseCreate} disabled={createLoading}>
              {t('common.cancel')}
            </Button>
            <PrimaryActionButton onClick={handleCreate} disabled={createLoading}>
              {createLoading ? <CircularProgress size={20} color="inherit" /> : t('planDiscountVouchers.actions.generate')}
            </PrimaryActionButton>
          </>
        )}
      >
        {createError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {createError}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {t('planDiscountVouchers.form.store')} *
          </Typography>
          <FormControl fullWidth error={Boolean(fieldErrors.storeId)}>
            <Select
              value={form.storeId}
              onChange={(event) => {
                setFieldErrors((current) => ({ ...current, storeId: undefined }));
                setForm((current) => ({ ...current, storeId: event.target.value }));
              }}
              displayEmpty
              MenuProps={{ disableScrollLock: true }}
              sx={selectSx}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography component="span" sx={{ color: PLACEHOLDER_COLOR, fontStyle: 'normal' }}>
                      {t('planDiscountVouchers.form.selectStore')}
                    </Typography>
                  );
                }

                const store = (stores || []).find((item) => (item.id ?? item.Id) === selected);
                return store?.name ?? store?.Name ?? selected;
              }}
            >
              {(stores || []).map((store) => (
                <MenuItem key={store.id ?? store.Id} value={store.id ?? store.Id}>
                  {store.name ?? store.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {fieldErrors.storeId && (
            <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
              {t(fieldErrors.storeId)}
            </Typography>
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {t('planDiscountVouchers.form.discountPercent')} *
          </Typography>
          <Box sx={inputWrapperSx}>
            <input
              type="number"
              min={1}
              max={100}
              step={1}
              value={form.discountPercent}
              placeholder={t('planDiscountVouchers.form.discountPlaceholder')}
              className={fieldErrors.discountPercent ? 'error' : undefined}
              onChange={(e) => {
                setFieldErrors((current) => ({ ...current, discountPercent: undefined }));
                setForm((current) => ({ ...current, discountPercent: e.target.value }));
              }}
            />
          </Box>
          {fieldErrors.discountPercent && (
            <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
              {t(fieldErrors.discountPercent)}
            </Typography>
          )}
        </Box>

        <SalovareDateRangePicker
          sx={{ mb: 2 }}
          startValue={form.startsAt}
          endValue={form.endsAt}
          disablePast
          onStartChange={(value) => {
            setDateRangeError(false);
            setFieldErrors((current) => ({ ...current, duration: undefined }));
            setForm((current) => ({ ...current, startsAt: value }));
          }}
          onEndChange={(value) => {
            setDateRangeError(false);
            setFieldErrors((current) => ({ ...current, duration: undefined }));
            setForm((current) => ({ ...current, endsAt: value }));
          }}
          helperText={fieldErrors.duration ? t(fieldErrors.duration) : t('planDiscountVouchers.form.endsAtHelper')}
          error={dateRangeError || Boolean(fieldErrors.duration)}
        />
      </ScrollableDialog>

      <ConfirmDialog
        open={Boolean(disableTarget)}
        title={t('planDiscountVouchers.disable.title')}
        message={t('planDiscountVouchers.disable.message', {
          code: disableTarget?.code ?? disableTarget?.Code ?? '',
        })}
        confirmText={t('planDiscountVouchers.actions.disable')}
        onConfirm={handleConfirmDisable}
        onClose={() => !disableLoading && setDisableTarget(null)}
        disableScrollLock
      />
    </>
  );
};

export default PlanDiscountVouchersPanel;
